import * as React from 'react';
import { Box, Grid } from '@material-ui/core';
import moment from 'moment';
import { isNullOrUndefined } from 'util';
import { CustomButton } from '../../custom-forms';
import { useTranslate } from '../../hooks/useTranslate';
import { Utils } from '../../helpers/Utils';
import { OwnStockCodeEnum } from '../../helpers/Constants';
import { Order } from '../../interfaces/Order';
import { CaseVehicleHistory } from '../../interfaces/Case';
import { IReferential } from '../../interfaces/IReferential';
import { Organization } from '../../interfaces/Organization';

export interface OrderDetailsHeaderProps {
  order: Order;
  vehicle: CaseVehicleHistory | undefined;
  costCenter: IReferential | null;
  interventionType: IReferential | null;
  partsLocationOrganization: Organization | null;
  country: IReferential | null;
  longDateFormat: string;
  saveButtonLabel: string | undefined;
  hideSave: boolean;
  saveDisabled: boolean;
  saveLoading: boolean;
  onSave: () => void;
}

const OrderDetailsHeader: React.FC<OrderDetailsHeaderProps> = ({
  order,
  vehicle,
  costCenter,
  interventionType,
  partsLocationOrganization,
  country,
  longDateFormat,
  saveButtonLabel,
  hideSave,
  saveDisabled,
  saveLoading,
  onSave
}) => {
  const t = useTranslate();
  const clientName = !isNullOrUndefined(order.client) ? order.client.name : '';
  const supplierDisplay = !isNullOrUndefined(order.supplier)
    ? ' - ' +
      (order.supplier.name.toUpperCase() === OwnStockCodeEnum.OWN_STOCK.toString()
        ? t('CALCULATION_DETAILS_OWN_STOCK_HEADER', 'Stoc Propriu')
        : order.supplier.name)
    : '';
  const deliveryAddress = !isNullOrUndefined(partsLocationOrganization)
    ? Utils.FormatFullDeliveryAddress(partsLocationOrganization, (k, d) => t(k, d), country)
    : null;
  const createdByUserName = !isNullOrUndefined(order.createdByUser)
    ? order.createdByUser.userName
    : '';

  return (
    <Box mb={2} px={2} py={3} style={{ backgroundColor: '#FFFFFF', borderRadius: 8 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={4}>
          <Box display="flex" alignItems="center">
            {!hideSave && (
              <CustomButton
                onClick={onSave}
                disabled={saveDisabled}
                isLoading={saveLoading}
                color="secondary"
              >
                {saveButtonLabel}
              </CustomButton>
            )}
            <Box ml={2} fontWeight={600}>
              <span>{order.id} </span>
              <span>{clientName}</span>
              <span>{supplierDisplay}</span>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          {!isNullOrUndefined(deliveryAddress) && (
            <Box fontWeight={600}>
              {t('ORDER_PART_DETAILS_SUPPLIER_LABEL11', 'Adresa livrare:') + ' ' + deliveryAddress}
            </Box>
          )}
          {!isNullOrUndefined(costCenter) && (
            <Box fontWeight={600}>
              {t('CASE_EVENT_COST_CENTER', 'Centru de cost') + ': ' + costCenter.name}
            </Box>
          )}
          {!isNullOrUndefined(interventionType) && (
            <Box fontWeight={600}>
              {t('CASE_EVENT_INTERVENTION_TYPE_LABEL', 'Tip Interventie') +
                ': ' +
                interventionType.displayName}
            </Box>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <Box
            display="flex"
            justifyContent="flex-end"
            flexWrap="nowrap"
            style={{ gap: 16, whiteSpace: 'nowrap' }}
          >
            <Box fontWeight={600}>
              {t('CALCULATION_DETAILS_VEHICAL_PLATE_NUMBER', 'Numar inmatriculare')}:{' '}
              {vehicle?.plateNumber}
            </Box>
            <Box fontWeight={600}>
              {t('CALCULATION_DETAILS_VIN', 'VIN')}: {vehicle?.vin}
            </Box>
            <Box fontWeight={600}>
              {t('CALCULATION_DETAILS_VEHICAL_NUMBER_OF_KM', 'Numar kilometri')}:{' '}
              {vehicle?.numberOfKilometers}
            </Box>
          </Box>
          <Box
            display="flex"
            justifyContent="flex-end"
            mt={1}
            style={{ color: '#7C8FAC', fontWeight: 600 }}
          >
            <span>{createdByUserName} </span>
            <span>&nbsp;{moment.utc(order.date).local().format(longDateFormat)}</span>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetailsHeader;
